import React, { PropsWithChildren } from 'react';
import { Dialog as DialogMUI, DialogActions, makeStyles, Theme } from '@material-ui/core';
import { Button, Header } from '@hwm/ui-lib';
import { Trans } from '@lingui/macro';

const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    padding: theme.spacing(5, 4, 2),
  },
}));

interface DialogModalProps extends PropsWithChildren {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  fullWidth?: boolean;
  dialogClass?: string;
  headerTitle: string;
  headerSubtitle?: string;
  headerDivider?: boolean;
  /**
   * each one to contain `dataTestId` name of a button (preferably `<name>-modal-header-buttons-<btn_name>-button`)
   * @example
   * [
   *  <Button dataTestId="test-modal-header-buttons-example-button">Example</Button>,
   *  <Button dataTestId="test-modal-header-buttons-another-button">Another</Button>,
   * ]
   */
  headerButtons?: JSX.Element[];
  /**
   * each one to contain `dataTestId` name of a button (preferably `<name>-modal-actions-<btn_name>-button`)
   * @example
   * [
   *  <Button dataTestId="test-modal-actions-example-button">Example</Button>,
   *  <Button dataTestId="test-modal-actions-another-button">Another</Button>,
   *  <Button dataTestId="test-modal-actions-close-button">Close</Button>,
   * ]
   */
  actionButtons?: JSX.Element[];
  /**
   * `dataTestId` name of a modal (preferably `<name>-modal`)
   * - children should contain `dataTestId` name of a modal content (preferably `<name>-modal-content`)
   * @example
   * ```tsx
   * <DialogModal {...props} dataTestId="test-modal">
   *  <div data-testid="test-modal-content">
   *    {...}
   *  </div>
   * </DialogModal>
   */
  dataTestId?: string;
}

export const DialogModal: React.FC<DialogModalProps> = ({
  show,
  setShow,
  fullWidth,
  dialogClass,
  headerTitle,
  headerSubtitle,
  headerDivider = false,
  headerButtons,
  actionButtons,
  dataTestId,
  children,
}) => {
  const classes = useStyles();

  return (
    <DialogMUI
      open={show}
      onClose={() => setShow(false)}
      maxWidth={fullWidth ? 'xs' : null}
      fullWidth={fullWidth}
      classes={{ paper: dialogClass ? dialogClass : classes.dialog }}
      data-testid={dataTestId}
    >
      <Header
        title={headerTitle}
        subtitle={headerSubtitle}
        showDivider={headerDivider}
        buttons={
          headerButtons ? (
            <>
              {headerButtons.map((btn, i) => (
                <React.Fragment key={i}>{btn}</React.Fragment>
              ))}
            </>
          ) : null
        }
        buttonsContainerClasses="flex justify-end"
        dataTestId={`${dataTestId}-header`}
      />
      {children}
      <DialogActions data-testid={`${dataTestId}-actions`}>
        {actionButtons?.map((btn, i) => <React.Fragment key={i}>{btn}</React.Fragment>)}
        <Button
          variant="outlined"
          style={{}}
          className={actionButtons ? '' : 'w-full'}
          onClick={() => setShow(false)}
          dataTestId={`${dataTestId}-actions-close-button`}
        >
          <Trans>Close</Trans>
        </Button>
      </DialogActions>
    </DialogMUI>
  );
};
